// Unified toast notifier for the B2B clients. Mirrors booking-client's notify
// idiom so both surfaces behave identically:
//   - One "Lost network connection" toast per outage (notifyNetworkError),
//     raised by the axios response interceptor. A module-level latch keeps it
//     from repeating and suppresses other error toasts for the outage window,
//     so a single failed request never stacks two toasts.
//   - Identical messages share a content-keyed id, so a repeat updates the
//     existing toast in place instead of piling up.
//
// Raise toasts through these helpers instead of importing react-hot-toast at
// call sites. Written with React.createElement (no JSX) so it stays a plain
// .js module regardless of the JSX loader config.

import { createElement } from 'react';
import toast, { Toaster } from 'react-hot-toast';

const NETWORK_TOAST_ID = 'network-error';
const NETWORK_OUTAGE_MS = 4000;

let networkErrorActive = false;

export const isNetworkErrorActive = () => {
  return networkErrorActive;
};

// Called by the axios interceptor on a network drop. Latches for the outage
// window so repeat failures (and per-call error toasts) stay quiet.
export const notifyNetworkError = () => {
  if (networkErrorActive) {
    return;
  }
  networkErrorActive = true;
  toast.error('Lost network connection', { id: NETWORK_TOAST_ID, duration: NETWORK_OUTAGE_MS });
  setTimeout(() => {
    networkErrorActive = false;
  }, NETWORK_OUTAGE_MS);
};

// Error toast. Suppressed while a network outage is latched (the interceptor
// owns that one toast) and deduped by message content.
export const notifyError = (message, options = {}) => {
  if (networkErrorActive) {
    return;
  }
  toast.error(message, { id: `err:${message}`, ...options });
};

// Compact error treatment for auth and other recoverable interruptions. It
// keeps the same dedupe/network-outage behavior as notifyError while avoiding
// an oversized alert over a sparse sign-in screen.
export const notifySubtleError = (message, options = {}) => {
  notifyError(message, {
    duration: 3500,
    style: {
      fontSize: '13px',
      lineHeight: '18px',
      maxWidth: '380px',
      padding: '10px 14px',
    },
    ...options,
  });
};

// Success toast, deduped by message content.
export const notifySuccess = (message, options = {}) => {
  toast.success(message, { id: `ok:${message}`, ...options });
};

// Shared <Toaster/> host. Cream surface + clay accents keep it on-brand for the
// portal; theme-neutral enough that the console can reuse it (dark shells can
// override per-toast options if needed).
export function ToastHost(props = {}) {
  return createElement(Toaster, {
    position: 'top-center',
    toastOptions: {
      style: {
        background: '#FFF9EE',
        color: '#3A2A22',
        border: '1px solid #E7DCCB',
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        fontSize: '13px',
        lineHeight: '18px',
        maxWidth: '380px',
        padding: '10px 14px',
      },
      success: { iconTheme: { primary: '#4A2C1D', secondary: '#FFF9EE' } },
      error: { iconTheme: { primary: '#E14E2C', secondary: '#FFF9EE' } },
    },
    ...props,
  });
}
